import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { useDarkMode } from "../../contexts/DarkModeContext";
import "../PostSignIn/Explore.css";
import "./SavedProfiles.css";

const SkeletonCard = ({ isDarkMode }) => (
  <div className={`explore-card skeleton-card${isDarkMode ? " dark" : ""}`}>
    <div className="skeleton skeleton-avatar" />
    <div className="skeleton-body">
      <div className="skeleton skeleton-line skeleton-name" />
      <div className="skeleton skeleton-line skeleton-sub" />
      <div className="skeleton skeleton-line skeleton-bio" />
      <div className="skeleton skeleton-line skeleton-bio short" />
    </div>
  </div>
);

const SavedProfiles = () => {
  const { isDarkMode } = useDarkMode();
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unsavingId, setUnsavingId] = useState(null);
  const navigate = useNavigate();
  const dm = isDarkMode ? "dark" : "";

  const fetchSavedProfiles = useCallback(async () => {
    setLoading(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setSavedProfiles([]);
      setLoading(false);
      return;
    }

    const { data: savedRows, error } = await supabase
      .from("saved_profiles")
      .select("id, saved_user_id, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error || !savedRows?.length) {
      setSavedProfiles([]);
      setLoading(false);
      return;
    }

    const savedIds = savedRows.map((r) => r.saved_user_id);

    const { data: profiles } = await supabase
      .from("user_profiles")
      .select(
        "user_id, full_name, profile_picture_url, location, church_name, bio2, bio1, service_types, ways_to_help"
      )
      .in("user_id", savedIds)
      .or("account_status.is.null,account_status.neq.deleted");

    // Preserve saved order (most recently saved first)
    const profileMap = Object.fromEntries(
      (profiles || []).map((p) => [p.user_id, p])
    );
    const ordered = savedRows
      .map((r) => profileMap[r.saved_user_id])
      .filter(Boolean);

    setSavedProfiles(ordered);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSavedProfiles();
  }, [fetchSavedProfiles]);

  const handleUnsave = async (profileUserId, e) => {
    e.stopPropagation();
    setUnsavingId(profileUserId);
    const {
      data: { user },
    } = await supabase.auth.getUser();

    await supabase
      .from("saved_profiles")
      .delete()
      .eq("user_id", user.id)
      .eq("saved_user_id", profileUserId);

    setSavedProfiles((prev) => prev.filter((p) => p.user_id !== profileUserId));
    setUnsavingId(null);
  };

  const handleViewProfile = (userId) => navigate(`/profile/${userId}`);

  const getDisplayTags = (user) => {
    const tags = [];
    if (user.ways_to_help?.length) {
      tags.push(...user.ways_to_help);
    } else if (user.service_types?.length) {
      tags.push(...user.service_types);
    }
    return tags.slice(0, 3);
  };

  return (
    <div className={`explore-root${dm ? " dark" : ""}`}>
      <Navbar />

      <div className={`explore-hero${dm ? " dark" : ""}`}>
        <div className="explore-hero-inner">
          <h1 className={`explore-heading${dm ? " dark" : ""}`}>
            Saved Profiles
          </h1>
          <p className={`saved-profiles-subtitle${dm ? " dark" : ""}`}>
            People you've bookmarked for quick access
          </p>
        </div>
      </div>

      <div className="explore-results-area">
        <div className="explore-col">
          {loading ? (
            <div className="explore-card-list">
              {[...Array(3)].map((_, i) => (
                <SkeletonCard key={i} isDarkMode={isDarkMode} />
              ))}
            </div>
          ) : savedProfiles.length === 0 ? (
            <div className={`saved-profiles-empty${dm ? " dark" : ""}`}>
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
              <p>No saved profiles yet</p>
              <p className="saved-profiles-empty-hint">
                Tap the bookmark icon on any profile to save it here.
              </p>
              <button
                className={`saved-profiles-explore-btn${dm ? " dark" : ""}`}
                onClick={() => navigate("/explore")}
              >
                Browse profiles
              </button>
            </div>
          ) : (
            <>
              <p className={`explore-count${dm ? " dark" : ""}`}>
                {savedProfiles.length}{" "}
                {savedProfiles.length === 1 ? "profile" : "profiles"} saved
              </p>
              <div className="explore-card-list">
                {savedProfiles.map((user) => {
                  const tags = getDisplayTags(user);
                  const extraTags =
                    (user.ways_to_help?.length ||
                      user.service_types?.length ||
                      0) - tags.length;

                  return (
                    <article
                      key={user.user_id}
                      className={`explore-card${dm ? " dark" : ""}`}
                      onClick={() => handleViewProfile(user.user_id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleViewProfile(user.user_id)
                      }
                    >
                      {/* Avatar */}
                      <div className={`explore-avatar${dm ? " dark" : ""}`}>
                        {user.profile_picture_url ? (
                          <img
                            src={user.profile_picture_url}
                            alt={`${user.full_name}'s profile`}
                            className="explore-avatar-img"
                          />
                        ) : (
                          <span className="explore-avatar-initial">
                            {user.full_name?.charAt(0) || "?"}
                          </span>
                        )}
                      </div>

                      {/* Body */}
                      <div className="explore-card-body">
                        <div className="explore-card-top">
                          <div className="explore-card-meta">
                            <h3
                              className={`explore-card-name${dm ? " dark" : ""}`}
                            >
                              {user.full_name}
                            </h3>
                            {user.bio21 && (
                              <span
                                className={`explore-card-badge${dm ? " dark" : ""}`}
                              >
                                {user.bio21}
                              </span>
                            )}
                          </div>

                          <div className="saved-profiles-card-actions">
                            {/* Unsave button */}
                            <button
                              className={`saved-profiles-unsave-btn${dm ? " dark" : ""}`}
                              onClick={(e) => handleUnsave(user.user_id, e)}
                              disabled={unsavingId === user.user_id}
                              title="Remove from saved"
                              aria-label="Remove from saved"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                              </svg>
                            </button>

                            {/* View button */}
                            <button
                              className={`explore-view-btn${dm ? " dark" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewProfile(user.user_id);
                              }}
                            >
                              View
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="9 18 15 12 9 6" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {(user.church_name || user.location) && (
                          <p
                            className={`explore-card-church${dm ? " dark" : ""}`}
                          >
                            {user.church_name}
                            {user.church_name && user.location && " · "}
                            {user.location}
                          </p>
                        )}

                        {user.bio2 && (
                          <p
                            className={`explore-card-bio${dm ? " dark" : ""}`}
                          >
                            {user.bio2}
                          </p>
                        )}

                        {tags.length > 0 && (
                          <div className="explore-card-tags">
                            {tags.map((tag) => (
                              <span
                                key={tag}
                                className={`explore-tag${dm ? " dark" : ""}`}
                              >
                                {tag}
                              </span>
                            ))}
                            {extraTags > 0 && (
                              <span
                                className={`explore-tag extra${dm ? " dark" : ""}`}
                              >
                                +{extraTags}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SavedProfiles;
